import { Capacitor } from '@capacitor/core'

/**
 * The API client.
 *
 * One place where the shape of the backend is known. Every call goes through `request`, which owns the
 * things that are easy to get wrong and expensive to get wrong repeatedly: the bearer token, the guest cart
 * identifier, the idempotency key, and turning an RFC 7807 problem response into something a screen can use.
 *
 * Nothing here computes a price. Totals come from the server, always, which is why the types below have
 * amounts as numbers the client displays and never as numbers it derives.
 */

/** A problem response, as the backend sends it. */
export interface ProblemDetail {
  type: string
  title: string
  status: number
  detail: string
  instance?: string
  correlationId?: string
  [key: string]: unknown
}

/** The shape of a failure, so a screen never has to inspect a raw response. */
export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetail
  readonly correlationId: string | undefined

  constructor(problem: ProblemDetail, correlationId?: string) {
    super(problem.detail || problem.title)
    this.name = 'ApiError'
    this.status = problem.status
    this.problem = problem
    this.correlationId = correlationId
  }

  /** The stable problem kind, for a screen that branches on it. */
  get code(): string {
    return this.problem.type?.split('/').pop() ?? 'unknown'
  }
}

/** Where the guest cart identifier is kept. A header rather than a cookie, so no CSRF surface. */
const CART_HEADER = 'X-Cart-Id'
const TOKEN_KEY = 'grocery.accessToken'
const REFRESH_KEY = 'grocery.refreshToken'
const CART_KEY = 'grocery.guestCartId'

export const tokens = {
  access: (): string | null => localStorage.getItem(TOKEN_KEY),
  refresh: (): string | null => localStorage.getItem(REFRESH_KEY),
  set(accessToken: string, refreshToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_KEY, refreshToken)
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

/** Generates a UUID v4 string safely, with fallback for non-secure HTTP contexts. */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const guestCart = {
  get: (): string | null => localStorage.getItem(CART_KEY),
  set: (cartId: string): void => localStorage.setItem(CART_KEY, cartId),
  clear: (): void => localStorage.removeItem(CART_KEY),
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Sent when an endpoint is idempotency keyed: order placement and payment initiation. */
  idempotencyKey?: string
  signal?: AbortSignal
}

/**
 * Performs a request and normalises the outcome.
 *
 * Retries once on a 401 after refreshing the token. A customer whose access token expired while they were
 * deciding what to buy should not be shown an error, and every screen would otherwise have to handle that.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options)

  if (response.status === 401 && tokens.refresh()) {
    const refreshed = await refreshAccessToken()

    if (refreshed) {
      return parse<T>(await send(path, options))
    }

    tokens.clear()
  }

  return parse<T>(response)
}

/** Base URL for API calls. In browser/web it stays empty (relative path); in mobile app/Capacitor it targets the AWS backend. */
export const API_BASE_URL: string =
  (import.meta.env.VITE_API_URL as string) ||
  (typeof window !== 'undefined' &&
  (Capacitor.isNativePlatform() ||
    window.location.protocol === 'capacitor:' ||
    (window.location.hostname === 'localhost' && window.location.port !== '5173'))
    ? 'http://3.109.124.162'
    : '')

export function buildApiUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  const base = (API_BASE_URL || '').replace(/\/+$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return base ? `${base}${cleanPath}` : cleanPath
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers = new Headers({ Accept: 'application/json' })

  const accessToken = tokens.access()

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  const cartId = guestCart.get()

  if (cartId && !accessToken) {
    headers.set(CART_HEADER, cartId)
  }

  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
  }

  if (options.idempotencyKey) {
    headers.set('Idempotency-Key', options.idempotencyKey)
  }

  // Built conditionally rather than with undefined values, because exactOptionalPropertyTypes treats an
  // explicit undefined as different from an absent property, and fetch cares about the difference.
  const init: RequestInit = { method: options.method ?? 'GET', headers }

  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body)
  }

  if (options.signal !== undefined) {
    init.signal = options.signal
  }

  const fullUrl = buildApiUrl(path)
  return fetch(fullUrl, init)
}

async function parse<T>(response: Response): Promise<T> {
  const cartId = response.headers.get(CART_HEADER)

  if (cartId) {
    guestCart.set(cartId)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('Content-Type') ?? ''
  const payload = contentType.includes('json') ? await response.json() : await response.text()

  if (!response.ok) {
    const problem: ProblemDetail =
      typeof payload === 'string'
        ? { type: 'about:blank', title: 'Request failed', status: response.status, detail: payload }
        : (payload as ProblemDetail)

    throw new ApiError(problem, problem.correlationId)
  }

  return payload as T
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = tokens.refresh()

  if (!refreshToken) {
    return false
  }

  try {
    const response = await fetch(buildApiUrl('/api/v1/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      return false
    }

    const tokensResponse = (await response.json()) as { accessToken: string; refreshToken: string }
    tokens.set(tokensResponse.accessToken, tokensResponse.refreshToken)

    return true
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Types, mirroring the backend's read models.
// ---------------------------------------------------------------------------

export interface CategoryTree {
  id: string
  slug: string
  name: string
  description?: string
  imageUrl?: string
  children: CategoryTree[]
}

export interface ProductSummary {
  id: string
  slug: string
  name: string
  brand?: string
  categorySlug: string
  primaryImageUrl?: string
  fromPricePaise: number
  maxMrpPaise: number
  variantCount: number
  requiresWeighing: boolean
}

export interface VariantView {
  id: string
  name: string
  sku: string
  unitCode: string
  packSize: number
  mrpPaise: number
  sellingPricePaise: number
  discountPercent: number
  requiresWeighing: boolean
  referenceWeightGrams?: number
}

export interface ProductDetail {
  id: string
  slug: string
  name: string
  description?: string
  brand?: string
  categorySlug: string
  categoryName: string
  hsnCode?: string
  gstRatePercent: number
  variants: VariantView[]
  images: { id: string; url: string; altText?: string; primary: boolean }[]
}

export interface CartLineView {
  variantId: string
  name: string
  sku: string
  unitCode: string
  quantity: number
  unitPricePaise: number
  lineTotalPaise: number
  requiresWeighing: boolean
  available: boolean
  availableQuantity: number
}

export interface CartView {
  cartId?: string
  customerId?: string
  lines: CartLineView[]
  couponCode?: string
  lineCount: number
  estimatedSubtotalPaise: number
  hasUnavailableLines: boolean
}

export interface QuoteIssue {
  code: string
  message: string
  subject?: string
}

export interface QuoteLine {
  variantId: string
  name: string
  quantity: number
  unitPricePaise: number
  netPaise: number
  discountPaise: number
  appliedPromotion?: string
  gstRatePercent: number
  availableQuantity: number
  fulfilable: boolean
}

export interface QuoteTotals {
  itemCount: number
  goodsSubtotalPaise: number
  lineDiscountsPaise: number
  couponDiscountPaise: number
  appliedCouponCode?: string
  deliveryFeePaise: number
  freeDelivery: boolean
  taxableValuePaise: number
  totalTaxPaise: number
  totalPaise: number
  taxBreakdown: { rateBasisPoints: number; taxablePaise: number; totalPaise: number }[]
  meetsMinimumOrderValue: boolean
  minimumOrderValuePaise: number
  amountShortOfMinimumPaise: number
}

export interface PaymentOption {
  method: 'ONLINE' | 'CASH_ON_DELIVERY'
  available: boolean
  unavailableReason?: string
  requiresOtpConfirmation: boolean
}

export interface CheckoutQuote {
  quoteId: string
  quotedAt: string
  expiresAt: string
  fulfilment: 'DELIVERY' | 'PICKUP'
  deliveryAddress?: { addressId: string; recipientName: string; formattedSingleLine: string }
  coverage?: { pincode: string; known: boolean; deliverable: boolean; codAvailable: boolean; areaName?: string }
  slot?: { slotId: string; label: string; date: string; remainingCapacity: number; bookable: boolean }
  lines: QuoteLine[]
  totals: QuoteTotals
  paymentOptions: PaymentOption[]
  issues: QuoteIssue[]
  orderable: boolean
}

export interface SlotView {
  id: string
  date: string
  startTime: string
  endTime: string
  type: 'DELIVERY' | 'PICKUP'
  remainingCapacity: number
  bookable: boolean
  full: boolean
  bookingClosed: boolean
}

export interface OrderSummary {
  orderId: string
  orderNumber: string
  status: string
  paymentStatus: string
  paymentMethod: string
  fulfilmentType: 'DELIVERY' | 'PICKUP'
  itemCount: number
  totalPaise: number
  amountPaidPaise: number
  totalsAdjusted: boolean
  cancellable: boolean
  placedAt: string
  window?: { slotId: string; date: string; label: string }
}

export interface OrderDetail extends OrderSummary {
  deliveryAddress?: { recipientName: string; phoneNumber: string; formattedSingleLine: string }
  totals: QuoteTotals
  lines: {
    lineNumber: number
    name: string
    quantity: number
    orderedQuantity: number
    finalQuantity: number
    quantityAdjusted: boolean
    unitPricePaise: number
    netPaise: number
    unitCode: string
  }[]
  history: { fromStatus?: string; toStatus: string; reason?: string; occurredAt: string }[]
  customerNote?: string
  cancellationReason?: string
  codConfirmationRequired: boolean
  currency: string
}

export interface Address {
  id: string
  label?: string
  recipientName: string
  phoneNumber: string
  line1: string
  line2?: string
  landmark?: string
  city: string
  state: string
  pincode: string
  defaultAddress: boolean
}

export interface PageResponse<T> {
  items: T[]
  nextCursor?: string
  hasMore: boolean
}

// ---------------------------------------------------------------------------
// Endpoints.
// ---------------------------------------------------------------------------

export const api = {
  catalogue: {
    categories: () => request<CategoryTree[]>('/api/v1/categories'),

    productsInCategory: (slug: string, cursor?: string) =>
      request<PageResponse<ProductSummary>>(
        `/api/v1/categories/${encodeURIComponent(slug)}/products${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      ),

    search: (query: string) =>
      request<ProductSummary[]>(`/api/v1/products/search?q=${encodeURIComponent(query)}`),

    product: (slug: string) => request<ProductDetail>(`/api/v1/products/${encodeURIComponent(slug)}`),

    availability: (variantIds: string[]) =>
      request<{ variantId: string; available: number; inStock: boolean }[]>(
        `/api/v1/inventory/availability?variantIds=${variantIds.join(',')}`,
      ),
  },

  auth: {
    requestCode: (phoneNumber: string) =>
      request<void>('/api/v1/auth/otp/request', { method: 'POST', body: { phoneNumber } }),

    verifyCode: (phoneNumber: string, code: string) =>
      request<{ accessToken: string; refreshToken: string; isNewCustomer: boolean }>(
        '/api/v1/auth/otp/verify',
        { method: 'POST', body: { phoneNumber, code } },
      ),

    loginWithPassword: (phoneNumber: string, password: string) =>
      request<{ accessToken: string; refreshToken: string; tokenType: string }>(
        '/api/v1/auth/login',
        { method: 'POST', body: { phoneNumber, password } },
      ),

    register: (phoneNumber: string, code: string, password: string, fullName?: string) =>
      request<{ accessToken: string; refreshToken: string; tokenType: string }>(
        '/api/v1/auth/register',
        { method: 'POST', body: { phoneNumber, code, password, fullName } },
      ),

    requestResetCode: (phoneNumber: string) =>
      request<{ expiresAt: string; expiresInSeconds: number }>(
        '/api/v1/auth/password/reset-code',
        { method: 'POST', body: { phoneNumber } },
      ),

    resetPassword: (phoneNumber: string, code: string, newPassword: string) =>
      request<{ accessToken: string; refreshToken: string; tokenType: string }>(
        '/api/v1/auth/password/reset',
        { method: 'POST', body: { phoneNumber, code, newPassword } },
      ),

    setPassword: (newPassword: string) =>
      request<{ phoneNumber: string; hasPassword: boolean }>('/api/v1/auth/password', {
        method: 'PUT',
        body: { newPassword },
      }),

    logout: (refreshToken: string) =>
      request<void>('/api/v1/auth/logout', { method: 'POST', body: { refreshToken } }),

    profile: () =>
      request<{ phoneNumber: string; email?: string; fullName?: string; id?: string }>('/api/v1/auth/me'),

    updateProfile: (data: { fullName?: string; email?: string }) =>
      request<{ phoneNumber: string; email?: string; fullName?: string; id?: string }>('/api/v1/auth/me', {
        method: 'PUT',
        body: data,
      }),

    mergeCart: (guestCartId: string) =>
      request<CartView>('/api/v1/cart/merge', { method: 'POST', body: { guestCartId } }),
  },

  addresses: {
    list: () => request<Address[]>('/api/v1/addresses'),

    create: (address: Omit<Address, 'id' | 'defaultAddress'> & { makeDefault?: boolean }) =>
      request<Address>('/api/v1/addresses', { method: 'POST', body: address }),

    remove: (addressId: string) =>
      request<void>(`/api/v1/addresses/${addressId}`, { method: 'DELETE' }),
  },

  serviceability: {
    check: (pincode: string) =>
      request<{ pincode: string; known: boolean; deliverable: boolean; codAvailable: boolean; areaName?: string }>(
        `/api/v1/serviceability/${pincode}`,
      ),
  },

  cart: {
    view: () => request<CartView>('/api/v1/cart'),

    add: (variantId: string, quantity: number) =>
      request<CartView>('/api/v1/cart/items', { method: 'POST', body: { variantId, quantity } }),

    setQuantity: (variantId: string, quantity: number) =>
      request<CartView>(`/api/v1/cart/items/${variantId}`, { method: 'PUT', body: { quantity } }),

    remove: (variantId: string) => request<CartView>(`/api/v1/cart/items/${variantId}`, { method: 'DELETE' }),

    applyCoupon: (code: string) => request<CartView>('/api/v1/cart/coupon', { method: 'POST', body: { code } }),

    removeCoupon: () => request<CartView>('/api/v1/cart/coupon', { method: 'DELETE' }),
  },

  checkout: {
    quote: (fulfilment: 'DELIVERY' | 'PICKUP', addressId?: string, slotId?: string) =>
      request<CheckoutQuote>('/api/v1/checkout/quote', {
        method: 'POST',
        body: { fulfilment, addressId, slotId },
      }),

    slots: (date: string, type: 'DELIVERY' | 'PICKUP') =>
      request<{ date: string; slots: SlotView[] }>(
        `/api/v1/slots?date=${date}&type=${type}&storeId=${storeIdFromEnv()}`,
      ),
  },

  orders: {
    /**
     * Places an order.
     *
     * The idempotency key is generated by the caller and reused on a retry, which is what makes a double tap
     * on a slow connection produce one order rather than two.
     */
    place: async (
      request_ : {
        fulfilment: 'DELIVERY' | 'PICKUP'
        addressId?: string
        slotId: string
        paymentMethod: 'ONLINE' | 'CASH_ON_DELIVERY'
        expectedTotalPaise: number
        quoteId?: string
        customerNote?: string
      },
      idempotencyKey: string,
    ): Promise<OrderDetail> => {
      const raw = await request<{ summary?: OrderSummary; orderId?: string } & OrderDetail>('/api/v1/orders', {
        method: 'POST',
        body: request_,
        idempotencyKey,
      })
      return raw.summary ? { ...raw.summary, ...raw } : raw
    },

    list: (cursor?: string) =>
      request<PageResponse<OrderSummary>>(`/api/v1/orders${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),

    find: async (orderId: string): Promise<OrderDetail> => {
      const raw = await request<{ summary?: OrderSummary; orderId?: string } & OrderDetail>(`/api/v1/orders/${orderId}`)
      return raw.summary ? { ...raw.summary, ...raw } : raw
    },

    cancel: async (orderId: string, reason?: string): Promise<OrderDetail> => {
      const raw = await request<{ summary?: OrderSummary; orderId?: string } & OrderDetail>(
        `/api/v1/orders/${orderId}/cancel`,
        { method: 'POST', body: { reason } },
      )
      return raw.summary ? { ...raw.summary, ...raw } : raw
    },

    updatePaymentMethod: async (
      orderId: string,
      paymentMethod: 'ONLINE' | 'CASH_ON_DELIVERY',
    ): Promise<OrderDetail> => {
      const raw = await request<{ summary?: OrderSummary; orderId?: string } & OrderDetail>(
        `/api/v1/orders/${orderId}/payment-method`,
        { method: 'POST', body: { paymentMethod } },
      )
      return raw.summary ? { ...raw.summary, ...raw } : raw
    },
  },

  payments: {
    initiate: (orderId: string, idempotencyKey: string) =>
      request<{
        paymentId: string
        amountPaise: number
        currency: string
        gatewayConfigured: boolean
        keyId?: string
        gatewayOrderId?: string
        existingPaymentReused: boolean
      }>('/api/v1/payments', { method: 'POST', body: { orderId }, idempotencyKey }),

    confirm: (gatewayOrderId: string, gatewayPaymentId: string, signature: string) =>
      request<{ paymentId: string; status: string }>('/api/v1/payments/confirm', {
        method: 'POST',
        body: { gatewayOrderId, gatewayPaymentId, signature },
      }),

    codEligibility: (orderId: string) =>
      request<{
        available: boolean
        unavailableReason?: string
        confirmationRequired: boolean
        limitPaise: number
      }>(`/api/v1/payments/cod/orders/${orderId}/eligibility`),
  },

  invoice: {
    /** The document is HTML, so it is opened in a tab rather than fetched into the app. */
    url: (orderId: string) => `/api/v1/orders/${orderId}/invoice`,
  },
}

/** The store identifier, which a single store deployment can supply at build time. */
export function storeIdFromEnv(): string {
  return import.meta.env.VITE_STORE_ID || 'a0000000-0000-0000-0000-000000000001'
}

/** Money for display. Never used for arithmetic: totals come from the server. */
export function formatMoney(paise: number): string {
  return `₹${(paise / 100).toFixed(2)}`
}

/** A quantity without trailing zeros, so 1.5 kg reads as 1.5 and 2 packs read as 2. */
export function formatQuantity(quantity: number): string {
  return Number(quantity.toFixed(3)).toString()
}
